import React from 'react';
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Zap, ShieldCheck, Timer, ArrowDownRight, Wind, Activity } from "lucide-react";

interface FilterSettingsProps {
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  row: (label: string, inputEl: React.ReactNode, helper?: string) => React.ReactNode;
}

export const FilterSettings: React.FC<FilterSettingsProps> = ({ form, setForm, row }) => {
  const toggle = (key: string) => setForm((f: any) => ({ ...f, [key]: !f[key] }));

  return (
    <div className="space-y-8">
      {/* Section 1: Trend Engine (Entry & Scaling Logic) */}
      <div className="pt-6 border-t border-border space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-black text-primary uppercase tracking-widest flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" /> 추세 대응 엔진 설정
          </h3>
          <Badge className="bg-primary/10 text-primary border-none text-[9px] font-black px-2 py-0.5">New Features</Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-primary/5 rounded-xl border border-transparent hover:border-primary/20 transition-all cursor-pointer" onClick={() => toggle('use_sequential_scaling')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-primary" checked={form.use_sequential_scaling} readOnly />
            <div className="flex-1">
              <p className="text-[12px] font-black text-foreground uppercase">순차적 진입 (Sequential Scaling)</p>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">한 번에 한 단계씩만 순차적으로 진입하여 시드를 보호합니다.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-primary/5 rounded-xl border border-transparent hover:border-primary/20 transition-all cursor-pointer" onClick={() => toggle('use_dynamic_targeting')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-primary" checked={form.use_dynamic_targeting} readOnly />
            <div className="flex-1">
              <p className="text-[12px] font-black text-foreground uppercase">동적 타겟 재계정 (Dynamic Re-targeting)</p>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">진입 지체 시 남은 단계들의 목표가를 현재가 기준으로 자동 조정합니다.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-amber-500/5 rounded-xl border border-transparent hover:border-amber-500/20 transition-all cursor-pointer" onClick={() => toggle('use_breakeven_timeout')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-amber-600" checked={form.use_breakeven_timeout} readOnly />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-1.5">
                <p className="text-[12px] font-black text-amber-600 uppercase">타임아웃 본전 탈출 (Timeout BE)</p>
                <Badge variant="outline" className="h-3.5 px-1.5 text-[8px] font-bold border-amber-500/30 text-amber-600 uppercase">Safety</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">고배수 진입 시 일정 시간 경과하면 목표가를 본전 부근으로 조기 설정합니다.</p>
              {form.use_breakeven_timeout && (
                <div className="pt-1.5" onClick={(e) => e.stopPropagation()}>
                  <p className="text-[9px] font-bold text-muted-foreground uppercase mb-1.5">목표 ROE 설정 (레버리지 적용 기준):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[1.6, 5.0, 3.0, 0.0].map((roe) => (
                      <label key={roe} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[10px] font-black cursor-pointer transition-all ${form.breakeven_target_roe === roe ? 'bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-500/20' : 'bg-background hover:border-amber-500/50 text-foreground border-border'}`}>
                        <input type="radio" name="breakeven_target_roe" className="sr-only" checked={form.breakeven_target_roe === roe} onChange={() => setForm((f: any) => ({ ...f, breakeven_target_roe: roe }))} />
                        {roe === 1.6 ? '1.6% (기본 수수료)' : `${roe}%`}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-primary/5 rounded-xl border border-transparent hover:border-primary/20 transition-all cursor-pointer" onClick={() => toggle('immediate_reentry')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-primary" checked={form.immediate_reentry} readOnly />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-[12px] font-black text-foreground uppercase">익절 후 즉시 재진입 (Immediate Re-entry)</p>
                <Badge className="bg-amber-500 text-white border-none text-[8px] h-3.5 px-1.5 font-black uppercase">NEW</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">익절 직후 지표 조건을 기다리지 않고 즉시 다음 사이클을 시작합니다.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-indigo-500/5 rounded-xl border border-transparent hover:border-indigo-500/20 transition-all cursor-pointer" onClick={() => toggle('one_way_mode')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-indigo-600" checked={form.one_way_mode} readOnly />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-[12px] font-black text-indigo-600 uppercase">양방향(Both) 시 단방향 제한 (One-Way Mode)</p>
                <Badge className="bg-indigo-500 text-white border-none text-[8px] h-3.5 px-1.5 font-black uppercase">Both</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">양방향(both) 상태일 때 한쪽 포지션이 돌면 반대편 진입을 차단합니다. 신규 진입 시 Super MA 점수 우세 방향 또는 1시간 추세(MA) 방향를 추종합니다.</p>
            </div>
          </div>

          {/* <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-primary/5 rounded-xl border border-transparent hover:border-primary/20 transition-all cursor-pointer" onClick={() => toggle('use_1h_trend_filter')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-primary" checked={form.use_1h_trend_filter} readOnly />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-[12px] font-black text-foreground uppercase">1시간봉 추세 필터 (1h EMA Trend Filter)</p>
                <Badge variant="outline" className="h-3.5 px-1.5 text-[8px] font-bold border-primary/30 text-primary uppercase">Safety</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">중기 추세(1h) 20/60/120 EMA가 정배열일 때만 진입 및 물타기를 허용합니다.</p>
            </div>
          </div> */}

          {/* <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-danger/5 rounded-xl border border-transparent hover:border-danger/20 transition-all cursor-pointer" onClick={() => toggle('use_1h_trend_sl')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-danger" checked={form.use_1h_trend_sl} readOnly />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-[12px] font-black text-danger uppercase">1시간봉 추세 전환 손절 (1h Trend Reverse SL)</p>
                <Badge variant="outline" className="h-3.5 px-1.5 text-[8px] font-bold border-danger/30 text-danger uppercase">Stop-Loss</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">포지션 보유 중 1시간봉 추세가 완전히 꺾이면 즉시 포지션을 정리합니다.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-success/5 rounded-xl border border-transparent hover:border-success/20 transition-all cursor-pointer" onClick={() => toggle('use_1h_trend_be')}>
            <input type="checkbox" className="mt-1 w-4 h-4 rounded text-success" checked={form.use_1h_trend_be} readOnly />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-[12px] font-black text-success uppercase">1시간봉 본절 탈출 모드 (1h Trend Break-even)</p>
                <Badge variant="outline" className="h-3.5 px-1.5 text-[8px] font-bold border-success/30 text-success uppercase">Smart Exit</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-snug">추세가 더 이상 유리하지 않다고 판단될 때 목표가를 본전으로 낮춰 탈출을 우선합니다.</p>
            </div>
          </div> */}

          <div className="flex items-start gap-3 p-3 bg-muted/20 hover:bg-primary/5 rounded-xl border border-transparent hover:border-primary/20 transition-all cursor-pointer" onClick={() => toggle('use_atr_scaling')}>
            <input type="checkbox" className="mt-0.5 w-4 h-4 rounded text-primary" checked={form.use_atr_scaling} readOnly />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-1.5">
                <p className="text-[12px] font-black text-foreground uppercase">ATR 동적 물타기 (ATR Dynamic Scaling)</p>
                <Badge className="bg-indigo-500 text-white border-none text-[7px] h-3.5">Hybrid</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground font-medium leading-tight">시장 변동성(ATR)에 따라 물타기 간격을 실시간으로 조절합니다.</p>
              {form.use_atr_scaling && (
                <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-[8px] font-bold text-muted-foreground uppercase">ATR배수:</span>
                      <input type="number" step="0.1" className="w-12 h-6 rounded border border-border px-1 text-[10px] font-black bg-background" value={form.atr_multiplier} onChange={(e) => setForm((f: any) => ({ ...f, atr_multiplier: Number(e.target.value) }))} />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[8px] font-bold text-muted-foreground uppercase">ATR기간:</span>
                      <input type="number" className="w-12 h-6 rounded border border-border px-1 text-[10px] font-black bg-background" value={form.atr_period} onChange={(e) => setForm((f: any) => ({ ...f, atr_period: Number(e.target.value) }))} />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[8px] font-bold text-muted-foreground uppercase shrink-0">제한방식:</span>
                      <select className="h-6 rounded border border-border px-1 text-[9px] font-black bg-background text-foreground cursor-pointer focus:outline-none w-[110px]" value={form.atr_limit_mode ?? 'strict'} onChange={(e) => setForm((f: any) => ({ ...f, atr_limit_mode: e.target.value }))}>
                        <option value="strict">Strict (원래간격 이하 제한)</option>
                        <option value="flexible">Flexible (원래간격 이하 허용)</option>
                      </select>
                    </div>
                    <div className={`flex items-center gap-1 ${form.atr_limit_mode !== 'flexible' ? 'opacity-40 pointer-events-none' : ''}`}>
                      <span className="text-[8px] font-bold text-muted-foreground uppercase shrink-0">최소보호갭:</span>
                      <select className="h-6 rounded border border-border px-1 text-[9px] font-black bg-background text-foreground cursor-pointer focus:outline-none" value={form.atr_min_gap_ratio ?? 0.5} onChange={(e) => setForm((f: any) => ({ ...f, atr_min_gap_ratio: Number(e.target.value) }))} disabled={form.atr_limit_mode !== 'flexible'}>
                        <option value={0.3}>30%</option>
                        <option value={0.5}>50%</option>
                        <option value={0.8}>80%</option>
                        <option value={1.0}>100%</option>
                      </select>
                    </div>
                  </div>
                  <div className="text-[9px] text-muted-foreground/80 font-medium leading-normal pt-1.5 space-y-0.5">
                    <div>* <b>Strict (안전)</b>: 변동성이 작아져도 원래 물타기 간격 이하로 촘촘해지는 것을 차단합니다.</div>
                    <div>* <b>Flexible (유연)</b>: 원래 간격보다 촘촘히 진입하되, 최소보호갭 하한선(%) 까지만 허용합니다.</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Core Trading Filters */}
      <div className="pt-6 border-t border-border space-y-6">
        <h3 className="text-xs font-black text-amber-500 uppercase tracking-widest flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> 진입 및 물타기 필터 (ENTRY & SCALING FILTERS)
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {row('EMA 추세 필터 (진입)', (
            <div className="flex items-center gap-3">
              <input type="checkbox" className="w-4 h-4 rounded-lg border-border text-primary" checked={form.use_ma_filter} onChange={(e) => setForm((f: any) => ({ ...f, use_ma_filter: e.target.checked }))} />
              <div className="flex-1 relative">
                <input type="number" className="w-full h-10 rounded-xl border border-border px-3 font-black text-sm bg-background disabled:opacity-30" disabled={!form.use_ma_filter} value={form.ma_period} onChange={(e) => setForm((f: any) => ({ ...f, ma_period: Number(e.target.value) }))} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground uppercase">PERIOD</span>
              </div>
            </div>
          ), '현재가가 해당 EMA 위에(롱) 혹은 아래에(숏) 있을 때만 진입합니다.')}

          {row('5MA 이격 필터 (진입)', (
            <div className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg hover:bg-primary/5 transition-all cursor-pointer" onClick={() => setForm((f: any) => ({ ...f, use_ma5_filter: !f.use_ma5_filter }))}>
              <input type="checkbox" className="w-4 h-4 rounded-lg border-border text-primary cursor-pointer" checked={form.use_ma5_filter || false} onChange={(e) => setForm((f: any) => ({ ...f, use_ma5_filter: e.target.checked }))} onClick={(e) => e.stopPropagation()} />
              <span className="text-[10px] font-bold text-foreground uppercase">5MA +0.1% 이하(롱) / -0.1% 이상(숏) 진입</span>
            </div>
          ), '상승중(롱)일 때는 5일선 + 0.1% 이하, 하락중(숏)일 때는 5일선 - 0.1% 이상에 가격이 위치할 때만 진입합니다.')}

          {row('RSI 모멘텀 필터 (진입)', (
            <div className="flex items-center gap-3">
              <input type="checkbox" className="w-4 h-4 rounded-lg border-border text-primary" checked={form.use_rsi_entry} onChange={(e) => setForm((f: any) => ({ ...f, use_rsi_entry: e.target.checked }))} />
              <div className="flex-1 relative">
                <input type="number" step="0.1" className="w-full h-10 rounded-xl border border-border px-3 text-sm font-black bg-background disabled:opacity-30" disabled={!form.use_rsi_entry} value={form.rsi_threshold} onChange={(e) => setForm((f: any) => ({ ...f, rsi_threshold: Number(e.target.value) }))} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground uppercase">THRESHOLD</span>
              </div>
            </div>
          ), 'RSI가 과매도(롱) 혹은 과매수(숏) 구간일 때만 진입합니다. (※ 진입 기회가 크게 제한되므로 평시에는 비활성화를 권장합니다.)')}

          {row('RSI 모멘텀 필터 (물타기)', (
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg hover:bg-primary/5 transition-all cursor-pointer" onClick={() => toggle('use_rsi_scaling')}>
                <input type="checkbox" className="w-4 h-4 rounded text-primary" checked={form.use_rsi_scaling} readOnly />
                <span className="text-[10px] font-bold text-foreground uppercase">물타기 시 임계값 도달 대기</span>
              </div>
              <p className="text-[9px] text-muted-foreground font-medium px-2 leading-tight">물타기(Scaling) 진입 시에도 RSI가 과매수/과매도 조건을 만족할 때까지 대기하여 더 유리한 평단가를 확보합니다. (※ 추가 물타기가 극도로 제한될 수 있으므로 주의해서 사용하십시오.)</p>
            </div>
          ))}

          <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {row('추세 억제 필터 (진입)', (
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg hover:bg-danger/5 transition-all cursor-pointer" onClick={() => toggle('use_1h_sideways_filter')}>
                  <input type="checkbox" className="w-4 h-4 rounded text-danger" checked={form.use_1h_sideways_filter} readOnly />
                  <span className="text-[10px] font-bold text-foreground uppercase">강한 추세 시 진입 차단</span>
                </div>
                <p className="text-[9px] text-muted-foreground font-medium px-2 leading-tight">미시 추세(15m/5m)가 급격할 때 역방향 진입을 차단합니다.</p>
              </div>
            ))}

            {row('추세 억제 필터 (물타기)', (
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg hover:bg-danger/5 transition-all cursor-pointer" onClick={() => toggle('use_1h_sideways_scaling_filter')}>
                  <input type="checkbox" className="w-4 h-4 rounded text-danger" checked={form.use_1h_sideways_scaling_filter} readOnly />
                  <span className="text-[10px] font-bold text-foreground uppercase">강한 추세 시 추가 매수 차단</span>
                </div>
                <p className="text-[9px] text-muted-foreground font-medium px-2 leading-tight">미시 추세(15m/5m)가 진행 중일 때 무리한 물타기로 자금이 고갈되는 것을 방지합니다.</p>
              </div>
            ))}
          </div>

          <div className="col-span-full p-3.5 rounded-xl bg-amber-500/5 border border-amber-500/10 text-amber-600/90 text-[10px] font-medium leading-relaxed space-y-1.5 shadow-sm">
            <div className="font-bold flex items-center gap-1 text-amber-500 uppercase tracking-wider text-[11px] mb-1">
              💡 추세 억제 필터 조합 권장 가이드
            </div>
            <p>
              * <b>Super MA 필터를 메인 추세 필터로 사용하는 경우</b>: 추세 억제 필터(진입/물타기)는 비활성화하는 것이 좋습니다. 거시적인 흐름(4h)에 따른 자금 관리(Slow Mode)가 DCA 전략 고유의 물타기 복구력을 더 매끄럽게 살려주기 때문입니다.
            </p>
            <p>
              * <b>단기 파동 중심의 역추세 매매를 하는 경우</b>: Super MA를 끄고, 단기 추세 억제 필터만 사용하여 쏟아지는 칼날을 단기적으로 피하는 타점을 노려야 정합성이 맞습니다.
            </p>
          </div>

          {/* [New] 그리드 스타일 진입 제한 가격 필터 */}
          <div className="col-span-full pt-4 border-t border-border/50">
            <div className="bg-success/5 border border-success/10 rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-success text-white border-none text-[9px] font-black px-2">GRID STYLE 매매</Badge>
                <h4 className="text-[11px] font-black text-success uppercase">진입 제한 가격 필터 (Entry Price Limit)</h4>
              </div>
              <p className="text-[9px] text-muted-foreground font-medium">특정 가격 범위 내에서만 신규 진입(롱/숏 첫 진입)을 허용하도록 제한합니다.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground font-bold">롱 진입 상한가 (이 가격 이하일 때만 롱 진입)</span>
                  <input
                    type="number"
                    step="any"
                    placeholder="미설정 (0)"
                    className="text-[10px] w-full h-10 rounded-xl border border-border px-3 font-black text-sm bg-background"
                    value={form.entry_price_limit_long || ''}
                    onChange={(e) => setForm((f: any) => ({ ...f, entry_price_limit_long: e.target.value === '' ? 0 : Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground font-bold">숏 진입 하한가 (이 가격 이상일 때만 숏 진입)</span>
                  <input
                    type="number"
                    step="any"
                    placeholder="미설정 (0)"
                    className="text-[10px] w-full h-10 rounded-xl border border-border px-3 font-black text-sm bg-background"
                    value={form.entry_price_limit_short || ''}
                    onChange={(e) => setForm((f: any) => ({ ...f, entry_price_limit_short: e.target.value === '' ? 0 : Number(e.target.value) }))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Super MA Filter Section */}
          <div className="col-span-full pt-4 border-t border-border/50">
            <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className="bg-indigo-600 text-white border-none text-[9px] font-black px-2">MACRO</Badge>
                  <h4 className="text-[11px] font-black text-indigo-600 uppercase">슈퍼 이평선 필터 (Super MA)</h4>
                </div>
                <input type="checkbox" className="w-5 h-5 rounded-lg border-indigo-600 text-indigo-600" checked={form.use_super_ma_filter} onChange={(e) => setForm((f: any) => ({ ...f, use_super_ma_filter: e.target.checked }))} />
              </div>
              {form.use_super_ma_filter && (
                <div className="space-y-4 pt-1 animate-in fade-in slide-in-from-top-1">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <p className="text-[9px] text-muted-foreground font-medium">초거시 추세(4h) EMA 정배열 점수(0~5)를 기반으로 시장 국면을 판별하여 위험 구간 진입을 차단합니다.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase">최소 점수:</span>
                      <input type="number" min="0" max="5" className="w-16 h-8 rounded-lg border border-indigo-500/30 px-2 text-xs font-black bg-background text-indigo-600" value={form.super_ma_score_threshold} onChange={(e) => setForm((f: any) => ({ ...f, super_ma_score_threshold: Number(e.target.value) }))} />
                    </div>
                  </div>

                  {/* 슈퍼 이평선 하위 제어 옵션 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-indigo-500/10">
                    <div className="flex items-start gap-2.5 p-2.5 bg-indigo-500/5 hover:bg-indigo-500/10 rounded-xl border border-indigo-500/10 hover:border-indigo-500/30 transition-all cursor-pointer" onClick={() => toggle('use_super_ma_first_entry')}>
                      <input type="checkbox" className="mt-0.5 w-4 h-4 rounded text-indigo-600" checked={form.use_super_ma_first_entry !== false} readOnly />
                      <div>
                        <p className="text-[10px] font-black text-indigo-600 uppercase">첫 진입 원천 차단 (First Entry Block)</p>
                        <p className="text-[9px] text-muted-foreground leading-snug mt-0.5">추세 점수가 설정값 미만일 때 신규 포지션 진입을 강하게 차단합니다.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5 p-2.5 bg-indigo-500/5 hover:bg-indigo-500/10 rounded-xl border border-indigo-500/10 hover:border-indigo-500/30 transition-all cursor-pointer" onClick={() => toggle('use_super_ma_scaling')}>
                      <input type="checkbox" className="mt-0.5 w-4 h-4 rounded text-indigo-600" checked={form.use_super_ma_scaling !== false} readOnly />
                      <div>
                        <p className="text-[10px] font-black text-indigo-600 uppercase">동적 물타기 제한 (Dynamic Limit)</p>
                        <p className="text-[9px] text-muted-foreground leading-snug mt-0.5">추세 점수가 낮을수록 최대 물타기 횟수를 자동으로 줄여 포지션 노출을 억제합니다.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5 p-2.5 bg-indigo-500/5 hover:bg-indigo-500/10 rounded-xl border border-indigo-500/10 hover:border-indigo-500/30 transition-all cursor-pointer" onClick={() => toggle('use_super_ma_be')}>
                      <input type="checkbox" className="mt-0.5 w-4 h-4 rounded text-indigo-600" checked={form.use_super_ma_be !== false} readOnly />
                      <div>
                        <p className="text-[10px] font-black text-indigo-600 uppercase">슈퍼 본전 탈출 (Super BE)</p>
                        <p className="text-[9px] text-muted-foreground leading-snug mt-0.5">점수가 2점 미만으로 하락 시 수익보다 탈출을 우선하여 목표가를 본전으로 낮춥니다.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5 p-2.5 bg-indigo-500/5 hover:bg-indigo-500/10 rounded-xl border border-indigo-500/10 hover:border-indigo-500/30 transition-all cursor-pointer" onClick={() => toggle('use_super_ma_slow_mode')}>
                      <input type="checkbox" className="mt-0.5 w-4 h-4 rounded text-indigo-600" checked={form.use_super_ma_slow_mode !== false} readOnly />
                      <div>
                        <p className="text-[10px] font-black text-indigo-600 uppercase">동적 비중 조절 (Slow Mode)</p>
                        <p className="text-[9px] text-muted-foreground leading-snug mt-0.5">슈퍼 점수에 비례하여 진입 금액을 자동 조절(60~100%)하여 효율적으로 시드를 배분합니다.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
